import { useState, useContext } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import Script from 'next/script'
import { useRouter } from 'next/router'
import useSWR, { useSWRConfig } from 'swr'
import { trackGoal } from 'fathom-client'

import { CartContext } from '@/context/cart'

import Navbar from 'components/navbar'
import Layout, { Textlayout } from 'components/layout'
import Seo from 'components/seo'
import Button from '@/components/ui/button'
import ShopifyImage from '@/components/shop/shopifyImage'
import Footer from 'components/footer'
// import { ProductCardRecommendation } from 'components/shop/productCard'

import { createCart, getCart, addToCart } from 'lib/shop/cartCommon'
import { getProduct, getProducts } from 'lib/shop/api/products'

import { ProductCardRecommendation } from '@/components/shop/productCard'

const fetcher = (...args) => fetch(...args).then((res) => res.json())

export default function productPage({ product, recommendations }) {
  const { mutate } = useSWRConfig()
  const [addedToBag, setAddedToBag] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showFullDesc, setShowFullDes] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [cartId, setCartId] = useContext(CartContext)

  const router = useRouter()

  if (router.isFallback) {
    return <div>loading</div>
  }

  const descFull = product.descriptionHtml
  const descPartial = `${product.description}`.substring(0, 200) + '...'

  const onAddToCart = ({ variantId }) => {
    if (!acceptedTerms) {
      return alert('Vous devez accepter les conditions générales de vente et renoncer à vos droits de rétractation.')
    }
    trackGoal('9M11SQQN') // ADD TO BAG
    window?.dataLayer?.push({
      event: 'addToBag',
      product_id: product.id,
      product_variant_id: variantId,
      product_price: product.variants.edges[0].node.priceV2.amount
    })
    setIsLoading(true)

    if (!cartId) {
      // CREATE CART WITH ITEM
      // SET CART ID
      createCart({ variantId: variantId })
        .then((cart) => {
          setCartId(cart.id)
          setAddedToBag(true)
          setIsLoading(false)
          mutate('/api/shop/cart?cartId=' + cart.id, { cart: cart })
          return
        })
        .catch((error) => {
          console.error('An error occured while creating cart.', error)
          setIsLoading(false)
        })
    } else {
      // CHECK IF LEGIT CART ID
      getCart({ cartId })
        .then((data) => {
          // CART GOOD
          addToCart({ cartId, variantId })
            .then((cart) => {
              setAddedToBag(true)
              setIsLoading(false)
              mutate('/api/shop/cart?cartId=' + cart.id, { cart: cart })

              return
            })
            .catch((error) => {
              console.error('An error occured while creating cart.', error)
              setIsLoading(false)
            })
        })
        .catch((error) => {
          // ERROR GETTING CART, CREATE NEW ONE WITH THE ITEM
          console.log('Current cart seems to have an error, creating a new one.', error)
          createCart({ variantId: variantId })
            .then((cart) => {
              setCartId(cart.id)
              setIsLoading(false)
              mutate('/api/shop/cart?cartId=' + cart.id, { cart: cart })

              return
            })
            .catch((error) => {
              console.error('An error occured while creating cart.', error)
              setIsLoading(false)
            })
          setIsLoading(false)
        })
      // yes -> ADD TO CART
      // no -> DO WHAT IS BEING DONE INSIDE THE IF
    }
  }

  const onBuyNow = ({ variantId }) => {
    if (!acceptedTerms) {
      return alert('Vous devez accepter les conditions générales de vente et renoncer à vos droits de rétractation.')
    }
    trackGoal('C7TKIDYP') // BUY NOW
    setIsLoading(true)

    createCart({ variantId, quantity: 1 })
      .then((cart) => {
        setIsLoading(false)
        window.location.href = cart.checkoutUrl
      })
      .catch((error) => {
        console.error('An error occured while creating cart.', error)
        setIsLoading(false)
      })
  }

  return (
    <>
      <Seo
        title={`${product.seo.title || product.title}`}
        description={`${
          product.seo.title || product.description.length > 0 ? product.description?.substring(0, 157) : ''
        }`}
        canonical={`/shop/product/${product.handle}`}
        imageSrc={product.featuredImage.url || null}
        imageHeight={product.featuredImage.height}
        imageWidth={product.featuredImage.width}
        schema={({ fullTitle, description, url }) => ({
          '@context': 'https://schema.org',
          '@type': 'Product',
          description: product.description,
          name: product.title,
          image: product.featuredImage.url,
          offers: {
            '@type': 'Offer',
            offeredBy: {
              '@type': 'Organization',
              '@id': 'https://ateliermilky.com#identity'
            },
            availability: 'https://schema.org/InStock',
            price: parseFloat(product.variants.edges[0].node.priceV2.amount).toFixed(2),
            priceCurrency: 'EUR'
          }
        })}
      />

      <div className={`${addedToBag ? 'h-screen overflow-hidden ' : ''} relative`}>
        {addedToBag ? <AddedToBagPopUp setAddedToBag={setAddedToBag} /> : null}
        <Navbar />
        <main className="mt-12 md:mt-24">
          <Layout>
            <Textlayout>
              <div className="grid gap-8 md:grid-cols-2">
                <div className="space-y-2">
                  {product.images
                    ? product.images.edges.map(({ node: thisImage }) => (
                        <div key={thisImage.id}>
                          <ShopifyImage imageData={thisImage} />
                        </div>
                      ))
                    : null}
                </div>
                <div>
                  {product.collections.edges.length > 0 ? (
                    <p className="text-lg uppercase">{product.collections.edges[0].node.title}</p>
                  ) : null}
                  <h1 className="mt-2 font-serif text-4xl font-bold">{product.title}</h1>
                  <div className="mt-4 font-medium">
                    {product.variants.edges[0].node.compareAtPrice ? (
                      <div className="text-sm font-light text-gray-700 line-through">
                        {parseFloat(product.variants.edges[0].node.compareAtPrice.amount).toLocaleString('fr-BE', {
                          style: 'currency',
                          currency: 'EUR'
                        })}
                      </div>
                    ) : null}
                    {parseFloat(product.variants.edges[0].node.priceV2.amount).toLocaleString('fr-BE', {
                      style: 'currency',
                      currency: 'EUR'
                    })}
                  </div>
                  <small className="block text-xs italic text-gray-500">TTC</small>

                  <div className="mt-4">
                    <label htmlFor="cgv">
                      <input
                        id="cgv"
                        type="checkbox"
                        className=""
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                      />{' '}
                      J'ai lu et j'accepte les{' '}
                      <Link href="/cgv">
                        <a className="underline">conditions générales de vente</a>
                      </Link>{' '}
                      et renonce à mon droit de rétractation.
                    </label>
                  </div>
                  <Button
                    onClick={(e) => {
                      onAddToCart({ variantId: product.variants.edges[0].node.id })
                    }}
                    isLoading={isLoading}
                    disabled={!product.variants.edges[0].node.availableForSale}
                    className="mt-2"
                    type="outline">
                    {product.variants.edges[0].node.availableForSale ? 'Ajouter au panier' : 'Plus de stock'}
                  </Button>

                  <Button
                    onClick={(e) => {
                      onBuyNow({ variantId: product.variants.edges[0].node.id })
                    }}
                    isLoading={isLoading}
                    disabled={!product.variants.edges[0].node.availableForSale}
                    className="mt-2"
                    type="plain">
                    {product.variants.edges[0].node.availableForSale ? 'Acheter maintenant' : 'Plus de stock'}
                  </Button>

                  {product.teamOrder?.value === 'true' ? (
                    <div className="mt-4 border-2 border-blue p-4 text-sm">
                      Besoin d'une facture ? Envoyez-nous un e-mail avec votre numéro de commande et les coordonnés de
                      votre entreprise (nom, numéro de TVA, adresse de siège social) à l'adresse{' '}
                      <a
                        className="font-medium underline"
                        href={'mailto:shop@ateliermilky.com?subject=Team order pour "' + product.title + '"'}>
                        shop@ateliermilky.com
                      </a>{' '}
                      après votre achat.
                    </div>
                  ) : null}

                  <div
                    className="rte mt-6 text-justify"
                    dangerouslySetInnerHTML={{ __html: showFullDesc ? descFull : descPartial }}
                  />

                  {product.description.length > 320 && !showFullDesc ? (
                    <div className="relative z-10 -mt-16 bg-gradient-to-t from-backgroundLight to-backgroundLight/0 pt-12 text-center">
                      <button
                        className="inline-block border-2 border-orange bg-backgroundLight px-3 py-1 text-sm font-medium uppercase text-orange"
                        onClick={() => setShowFullDes(true)}>
                        lire plus
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="mt-24 border border-black p-8">
                <h2 className="font-medium uppercase">Vous aimerez également</h2>
                <div className="mt-4 grid gap-8 md:grid-cols-3">
                  {recommendations.map(({ node: thisProduct }) => (
                    <ProductCardRecommendation
                      key={thisProduct.id}
                      name={thisProduct.title}
                      href={`/shop/product/${thisProduct.handle}`}
                      imageData={thisProduct.featuredImage}
                    />
                  ))}
                </div>
              </div>
            </Textlayout>
          </Layout>
        </main>
        <div className="mt-24">
          <Footer />
        </div>
      </div>
    </>
  )
}

const AddedToBagPopUp = ({ setAddedToBag }) => {
  const [cartId, setCartId] = useContext(CartContext)
  const { data, error } = useSWR('/api/shop/cart?cartId=' + cartId, fetcher, { revalidateOnFocus: false })

  return (
    <div className="absolute top-0 left-0 z-20 flex h-full w-full items-center justify-center bg-orange-50/50 backdrop-blur-none">
      <div className="z-20 m-4 bg-white py-4 px-6">
        <h2 className="text-lg font-medium">Ajouté au panier !</h2>

        <div className="mt-4 flex space-x-4">
          <div>
            <Button type="outline" className="w-full" isLoading={error || !data?.cart?.checkoutUrl} href="/shop/panier">
              Voir mon panier{' '}
              <span>
                (
                {!error && data?.cart?.lines?.edges
                  ? data?.cart?.lines?.edges.reduce((prev, current) => (prev += current.node.quantity), 0)
                  : ' '}
                )
              </span>
            </Button>
          </div>
          <div>
            <Button
              type="plain"
              isLoading={error || !data?.cart?.checkoutUrl}
              href={data?.cart?.checkoutUrl || '/shop/panier'}>
              Passer commande
            </Button>
          </div>
        </div>
      </div>
      <button className="absolute top-0 left-0 z-10 h-full w-full object-cover" onClick={() => setAddedToBag(false)} />
    </div>
  )
}

export async function getStaticProps(context) {
  const { product, products } = await getProduct({ handle: context.params.productSlug })

  const shuffledProducts = products.edges
    .filter(({ node: thisProduct }) => thisProduct.id !== product.id)
    .sort(() => 0.5 - Math.random())
  const threeRandomProducts = shuffledProducts.slice(0, 3)

  return {
    props: {
      product: product,
      recommendations: threeRandomProducts
    },
    revalidate: 60 * 5
  }
}

export async function getStaticPaths() {
  const products = await getProducts()

  return {
    paths: products.edges.map(({ node: thisProduct }) => ({ params: { productSlug: thisProduct.handle } })),
    fallback: true
  }
}
